import { RightEnum, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Tooltip } from "antd"
import React from "react"
import { AlertTriangle } from "react-feather"
import { useTranslation } from "react-i18next"
import { generatePath, Link } from "react-router-dom"
import { DeletedTooltip } from "../../../../../components/deletedTooltip"
import { VendorSelect } from "../../../../../components/forms/expenseForm/commonInputFields/VendorSelect"
import { useUserCreditors } from "../../../../../shared/hooks/vendor.hooks"
import { RoutePathEnum } from "../../../../../shared/router/RoutePath.enum"
import { AuthzService } from "../../../../../shared/services"
import { getTooltipPopupContainer } from "../../../../../shared/utils/helper.utils"
import { UserCreditor } from "../../../../../shared/utils/vendor.utils"

export interface VendorFieldProps {
    // Current creditor data
    currentCreditor?: UserCreditor | Vendor | undefined
    embeddedCreditor?: { name?: string; creditorNumber?: string } | undefined
    isUserCreditor?: boolean
    // Selection props
    selectedCreditorId?: string
    selectedVendorType?: VendorTypeEnum
    shouldRenderSelectDropdown?: boolean
    // Callbacks
    onVendorUpdate?: (value: string, vendorType: VendorTypeEnum) => void
    // State
    isReadOnly?: boolean
    areInputsDisabled?: boolean
    isLoading?: boolean
    // Display options
    showUserCreditors?: boolean
}

export const VendorField = ({
    currentCreditor,
    embeddedCreditor,
    isUserCreditor = false,
    selectedCreditorId = "",
    selectedVendorType = VendorTypeEnum.VENDOR,
    shouldRenderSelectDropdown = false,
    onVendorUpdate,
    isReadOnly = false,
    areInputsDisabled = false,
    isLoading = false,
    showUserCreditors = true,
}: VendorFieldProps) => {
    const { t } = useTranslation()
    const userCreditors = useUserCreditors(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ) && showUserCreditors)

    const VendorDetails = () => {
        if (!currentCreditor || !embeddedCreditor) {
            return null
        }
        // early return if user has no vendor view right
        if (!AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__READ)) {
            return <span className="text">{embeddedCreditor.name}</span>
        }

        const redirectDestination = isUserCreditor ? RoutePathEnum.EMPLOYEES : RoutePathEnum.VENDORS
        const vendorId = (currentCreditor as UserCreditor | Vendor)?.id
        const VendorLink = (
            <Link
                className="block truncate max-w-full"
                to={generatePath(redirectDestination === RoutePathEnum.EMPLOYEES ? RoutePathEnum.EMPLOYEES : RoutePathEnum.VENDOR_DETAIL_PAGE, { id: vendorId! })}
            >
                {embeddedCreditor.name}
            </Link>
        )

        // For expenses, handle user creditors
        const expenseCreditor = currentCreditor as UserCreditor | Vendor
        return embeddedCreditor?.creditorNumber ? (
            <div className="flex gap-4 min-w-0 max-w-full">
                {expenseCreditor.deleted && <DeletedTooltip translationKey="tooltips:vendor.was_deleted" deletionDate={expenseCreditor?.dateDeleted} />}
                {VendorLink}
            </div>
        ) : (
            <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("info:employee_no_creditor_number")}>
                <div className="flex gap-4 justify-end min-w-0">
                    <AlertTriangle className="px-2 mt-2 flex-shrink-0" fill="#FBAC3B" size={18} stroke="#FFFFFF" />
                    {VendorLink}
                </div>
            </Tooltip>
        )
    }

    // Determine if we should show the select or the details
    const shouldShowSelect = () => (isReadOnly ? false : !currentCreditor || !(isUserCreditor || (currentCreditor as Vendor)?.isApproved))

    const shouldShowDetails = () => currentCreditor && (isUserCreditor || (currentCreditor as Vendor)?.isApproved)

    return (
        <li className="flex justify-between items-start">
            <span className="title">{t("label:vendor")}</span>
            {shouldShowDetails() ? (
                <VendorDetails />
            ) : shouldShowSelect() ? (
                <span className="w-1/2">
                    <VendorSelect
                        shouldShowOnlyVerifiedVendors={false}
                        shouldRenderSelectDropdown={shouldRenderSelectDropdown}
                        selectedVendorType={selectedVendorType}
                        userCreditors={userCreditors}
                        selectedCreditorId={selectedCreditorId}
                        onSelect={onVendorUpdate}
                        disabled={isLoading || areInputsDisabled}
                        isLoading={isLoading}
                        defaultNonSelectedValue={true}
                        popupClassName="min-w-200"
                    />
                </span>
            ) : (
                <span className="text truncate">-</span>
            )}
        </li>
    )
}
